import { FC } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { GapByCountyStatusBreakdown } from "@app/network-portfolio-management/interfaces";
import { formatCount } from "@app/network-portfolio-management/utils/formatUtils";
import { GapByCountyStatusSkeleton } from "@app/network-portfolio-management/components/GapByCountyStatusSkeleton";

interface GapByCountyStatusProps {
  projectId: number;
  breakdown: GapByCountyStatusBreakdown | null | undefined;
  isLoading: boolean;
  hasError: boolean;
}

const HELP_TEXT = "Total Gap Count in the Adequacy Overview above considers all gaps in your current service area (Active, Pending, and Approved), but does not include Opportunity gaps.";

interface StatusKeyConfig {
  colorModifier: string;
  countKey: keyof GapByCountyStatusBreakdown;
  label: string;
}

// Fixed AC order and color treatment (colorModifier drives the SCSS `--active`/`--pending`/`--approved`/
// `--opportunity` modifier classes) — do not reorder.
const STATUS_KEYS: readonly StatusKeyConfig[] = [
  { colorModifier: "active", countKey: "active", label: "Active" },
  { colorModifier: "pending", countKey: "pending", label: "Pending" },
  { colorModifier: "approved", countKey: "approved", label: "Approved" },
  { colorModifier: "opportunity", countKey: "opportunity", label: "Opportunity" },
];

// Replaces the single "Total" cell previously inlined in ProjectCardMetrics.tsx. Data is fetched
// independently of the rest of the card (progressive load - see ProjectInfiniteList's per-page batched
// query), so the section always renders immediately and only its body swaps between skeleton/error/counts.
export const GapByCountyStatus: FC<GapByCountyStatusProps> = ({ projectId, breakdown, isLoading, hasError }) => (
  <section
    className="network-portfolio-management-project-card__gap-status"
    data-testid={`network-portfolio-management-card-gap-${projectId}`}
    aria-labelledby={`gap-section-label-${projectId}`}
  >
    <div className="network-portfolio-management-project-card__gap-status-header">
      <span id={`gap-section-label-${projectId}`} className="network-portfolio-management-project-card__gap-section-label">
        Gap by County Status
      </span>
      <TooltipWrapper position="top" text={HELP_TEXT} tooltipWidth="20rem">
        <div
          className="network-portfolio-management-project-card__gap-status-info"
          data-testid={`network-portfolio-management-card-gap-status-info-${projectId}`}
        >
          <QesIcon description="Info" iconName={QesIcons.InfoCircle} />
        </div>
      </TooltipWrapper>
    </div>

    {hasError ? (
      <p className="network-portfolio-management-project-card__gap-status-error" data-testid={`network-portfolio-management-card-gap-error-${projectId}`}>
        There was a problem loading your data. Please try again.
      </p>
    ) : isLoading ? (
      <GapByCountyStatusSkeleton />
    ) : (
      <div className="network-portfolio-management-project-card__gap-status-row">
        {STATUS_KEYS.map(({ colorModifier, countKey, label }) => (
          <div
            className="network-portfolio-management-project-card__gap-status-cell"
            data-testid={`network-portfolio-management-card-gap-cell-${projectId}-${colorModifier}`}
            key={label}
          >
            <span className="network-portfolio-management-project-card__gap-status-label">{label}</span>
            <span
              className={`network-portfolio-management-project-card__gap-status-num network-portfolio-management-project-card__gap-status-num--${colorModifier}`}
            >
              {formatCount(breakdown?.[countKey] ?? null)}
            </span>
          </div>
        ))}
      </div>
    )}
  </section>
);
